import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Loading, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UserRoundCheck, Plus, Pencil, Upload, FileDown, KeyRound, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { onErr, downloadFile } from "@/lib/providers";

const empty = {
  name: "", provider_type: "Individual", mobile: "", email: "", address: "", city: "", nature_of_service: "",
  pan: "", gst_registered: false, gstin: "", bank_account_name: "", bank_account: "", ifsc: "",
  checklist: {}, conversion_reason: "", conversion_signoff_by: "", status: "Active",
};

export default function ProvidersTab() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(empty);
  const [conv, setConv] = useState(null);
  const [loginFor, setLoginFor] = useState(null);
  const [pwd, setPwd] = useState("");
  const fileRef = useRef(null);
  const [uploadFor, setUploadFor] = useState(null);

  const { data: providers, isLoading } = useQuery({ queryKey: ["providers"], queryFn: async () => (await api.get("/service-providers")).data });
  const { data: items } = useQuery({ queryKey: ["providerChecklist"], queryFn: async () => (await api.get("/service-providers/checklist-items")).data });

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const startNew = () => { setEditId(null); setF(empty); setConv(null); setOpen(true); };
  const startEdit = (p) => {
    setEditId(p.id);
    setF({ ...empty, ...p, checklist: p.checklist || {}, conversion_reason: p.conversion?.reason || "", conversion_signoff_by: p.conversion?.signoff_by || "" });
    setConv(p.conversion?.status && p.conversion.status !== "clear" ? p.conversion : null);
    setOpen(true);
  };

  const precheck = async () => {
    if (!f.pan && (f.mobile || "").replace(/\D/g, "").length < 10) return;
    try {
      const { data } = await api.post("/service-providers/precheck", { pan: f.pan, mobile: f.mobile });
      setConv(data.status === "clear" ? null : data);
    } catch { /* non-blocking */ }
  };

  const save = useMutation({
    mutationFn: async () => (editId ? await api.put(`/service-providers/${editId}`, f) : await api.post("/service-providers", f)).data,
    onSuccess: (d) => {
      toast.success(editId ? "Provider updated" : `Provider ${d.provider_code} created`);
      qc.invalidateQueries({ queryKey: ["providers"] }); setOpen(false);
    },
    onError: onErr,
  });

  const upload = useMutation({
    mutationFn: async ({ id, file }) => {
      const fd = new FormData(); fd.append("file", file);
      return (await api.post(`/service-providers/${id}/agreement`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: () => { toast.success("Agreement uploaded"); qc.invalidateQueries({ queryKey: ["providers"] }); },
    onError: onErr,
  });

  const login = useMutation({
    mutationFn: async () => {
      const path = loginFor.has_login ? "reset-password" : "create-login";
      return (await api.post(`/service-providers/${loginFor.id}/${path}`, { password: pwd })).data;
    },
    onSuccess: () => {
      toast.success(loginFor.has_login ? "Password reset" : `Login created — username ${loginFor.provider_code}`);
      qc.invalidateQueries({ queryKey: ["providers"] }); setLoginFor(null); setPwd("");
    },
    onError: onErr,
  });

  const blocked = conv?.status === "blocked";

  return (
    <div>
      <div className="flex justify-between items-center mb-4 gap-3">
        <p className="text-sm text-slate-500">Independent contractors only. They never appear in payroll, attendance, leave or policies.</p>
        <Button className="bg-navy hover:bg-navy-700 shrink-0" onClick={startNew} data-testid="add-provider"><Plus className="h-4 w-4 mr-1" /> New Provider</Button>
      </div>
      <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => {
        const file = e.target.files?.[0]; e.target.value = "";
        if (file && uploadFor) upload.mutate({ id: uploadFor, file });
      }} />
      {isLoading ? <Loading /> : !providers?.length ? (
        <EmptyState icon={UserRoundCheck} title="No service providers yet" hint="Add a provider, complete the onboarding checklist, then issue a work order." />
      ) : (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              {["Code", "Name", "Nature of service", "PAN / GST", "Onboarding", "Login", "Status", ""].map((h) => <TableHead key={h} className="overline text-slate-500">{h}</TableHead>)}
            </TableRow></TableHeader>
            <TableBody>
              {providers.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-mono text-sm text-teal">{p.provider_code}</TableCell>
                  <TableCell className="font-medium text-navy">{p.name}<div className="text-xs text-slate-400">{p.mobile}{p.city ? ` · ${p.city}` : ""}</div></TableCell>
                  <TableCell className="text-sm text-slate-600 max-w-[220px]">{p.nature_of_service}</TableCell>
                  <TableCell className="text-xs text-slate-600">{p.pan || "No PAN"}<div>{p.gst_registered ? p.gstin : "GST: not registered"}</div></TableCell>
                  <TableCell>
                    {p.onboarding_gaps.length === 0
                      ? <span className="text-emerald-700 text-xs font-semibold inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Complete</span>
                      : <span className="text-amber-700 text-xs font-semibold" title={p.onboarding_gaps.join("\n")}>{p.onboarding_gaps.length} item(s) open</span>}
                    {p.conversion?.status === "needs_signoff" && <div className="text-[11px] text-amber-700">Former employee {p.conversion.employee_id}</div>}
                  </TableCell>
                  <TableCell className="text-xs">{p.has_login ? <span className="text-emerald-700">{p.provider_code}</span> : <span className="text-slate-400">None</span>}</TableCell>
                  <TableCell><StatusBadge status={p.status} /></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" title="Edit" onClick={() => startEdit(p)}><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" title="Upload signed agreement" onClick={() => { setUploadFor(p.id); fileRef.current?.click(); }}><Upload className="h-4 w-4" /></Button>
                    {p.has_agreement && <Button size="sm" variant="ghost" title="Download agreement" onClick={() => downloadFile(`/service-providers/${p.id}/agreement`, `Agreement_${p.provider_code}`)}><FileDown className="h-4 w-4" /></Button>}
                    <Button size="sm" variant="ghost" title={p.has_login ? "Reset password" : "Create login"} onClick={() => { setLoginFor(p); setPwd(""); }}><KeyRound className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? `Edit ${f.provider_code || "Provider"}` : "New Service Provider"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Name *</Label><Input className="mt-1" value={f.name} onChange={(e) => set("name", e.target.value)} /></div>
            <div>
              <Label>Type</Label>
              <Select value={f.provider_type} onValueChange={(v) => set("provider_type", v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="Individual">Individual</SelectItem><SelectItem value="Firm">Firm / Agency</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Mobile *</Label><Input className="mt-1" value={f.mobile} onChange={(e) => set("mobile", e.target.value)} onBlur={precheck} /></div>
            <div><Label>Email</Label><Input className="mt-1" value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
            <div className="sm:col-span-2"><Label>Nature of service / scope *</Label><Input className="mt-1" value={f.nature_of_service} onChange={(e) => set("nature_of_service", e.target.value)} placeholder="Community TB screening (door-to-door)" /></div>
            <div><Label>City</Label><Input className="mt-1" value={f.city} onChange={(e) => set("city", e.target.value)} /></div>
            <div><Label>Address</Label><Input className="mt-1" value={f.address} onChange={(e) => set("address", e.target.value)} /></div>
            <div><Label>PAN</Label><Input className="mt-1 uppercase" value={f.pan} onChange={(e) => set("pan", e.target.value.toUpperCase())} onBlur={precheck} /></div>
            <div className="flex items-end gap-3 pb-2"><Switch checked={f.gst_registered} onCheckedChange={(v) => set("gst_registered", v)} /><Label>GST registered</Label></div>
            {f.gst_registered && <div className="sm:col-span-2"><Label>GSTIN *</Label><Input className="mt-1 uppercase" value={f.gstin} onChange={(e) => set("gstin", e.target.value.toUpperCase())} /></div>}
            <div><Label>Bank account name</Label><Input className="mt-1" value={f.bank_account_name} onChange={(e) => set("bank_account_name", e.target.value)} /></div>
            <div><Label>Bank account no.</Label><Input className="mt-1" value={f.bank_account} onChange={(e) => set("bank_account", e.target.value)} /></div>
            <div><Label>IFSC</Label><Input className="mt-1 uppercase" value={f.ifsc} onChange={(e) => set("ifsc", e.target.value.toUpperCase())} /></div>
            <div>
              <Label>Status</Label>
              <Select value={f.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="Active">Active</SelectItem><SelectItem value="Inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
          </div>

          {conv && (
            <div className={`mt-3 rounded-md border p-3 text-sm ${blocked ? "border-red-200 bg-red-50 text-red-800" : "border-amber-200 bg-amber-50 text-amber-900"}`}>
              <p className="flex items-start gap-2"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />{conv.message}</p>
              {!blocked && (
                <div className="grid sm:grid-cols-2 gap-3 mt-3">
                  <div><Label>Reason for engaging as provider *</Label><Input className="mt-1 bg-white" value={f.conversion_reason} onChange={(e) => set("conversion_reason", e.target.value)} /></div>
                  <div><Label>Approved by (legal / CA) *</Label><Input className="mt-1 bg-white" value={f.conversion_signoff_by} onChange={(e) => set("conversion_signoff_by", e.target.value)} /></div>
                </div>
              )}
            </div>
          )}

          <div className="mt-4 border-t pt-3">
            <Label>Onboarding checklist — all required before a work order can be issued</Label>
            <div className="mt-2 space-y-2">
              {(items || []).map((it) => (
                <label key={it.key} className="flex items-start gap-2 text-sm text-slate-700">
                  <Checkbox className="mt-0.5" checked={!!f.checklist[it.key]} onCheckedChange={(v) => set("checklist", { ...f.checklist, [it.key]: !!v })} />
                  {it.label}
                </label>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-2">Upload the signed agreement from the provider list after saving.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={blocked || !f.name || !f.mobile || !f.nature_of_service || save.isPending} onClick={() => save.mutate()}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!loginFor} onOpenChange={(o) => !o && setLoginFor(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>{loginFor?.has_login ? "Reset password" : "Create provider login"}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Username: <span className="font-mono text-navy">{loginFor?.provider_code}</span>. The provider sees only their work orders, field logs and payout advices.</p>
          <div><Label>Password</Label><Input type="text" className="mt-1" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Min 8 chars, 1 capital, 1 special" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLoginFor(null)}>Cancel</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={pwd.length < 8 || login.isPending} onClick={() => login.mutate()}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
